// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

export type ClassName =
  | string
  | number
  | null
  | undefined
  | false
  | ClassName[]
  | { [prop: string]: boolean | null | undefined };

/**
 * Joins class name fragments into a single space-separated string.
 *
 * @remarks
 * Use this helper for every conditional or combined `className` in application
 * code. Do not use template literals or string concatenation for class lists.
 *
 * Falsy values (`false`, `null`, `undefined`, `0`, `''`) are skipped. Object keys
 * are included when their value is truthy. Returns `undefined` when no classes
 * remain (safe for React `className` props).
 * @example
 *
 * ```ts
 * classNames('px-4', 'py-2');
 * // => 'px-4 py-2'
 * ```
 *
 * @example
 *
 * ```ts
 * classNames('base', isActive && 'ring-2', isDisabled && 'opacity-50');
 * // => 'base ring-2' when isActive is true and isDisabled is false
 * ```
 *
 * @example
 *
 * ```ts
 * classNames(styles['field-stack'], 'md:col-span-2');
 * // => 'field-stack md:col-span-2'
 * ```
 *
 * @example
 *
 * ```ts
 * classNames(styles.card, { [styles.selected]: isSelected });
 * // => 'card selected' when isSelected is true
 * ```
 */
export const classNames = (...values: ClassName[]): string | undefined => {
  const classNamesRec = (classes: ClassName[]): string =>
    classes.reduce<string>((current, next) => {
      switch (typeof next) {
        case 'object':
          if (next === null) return current;

          if (Array.isArray(next)) return classNamesRec([current, ...next]);

          return Object.entries(next).reduce(
            (className, [key, value]) =>
              className.concat(value ? ` ${key}` : '').trim(),
            current,
          );
        case 'string':
        case 'number':
          if (next) return `${current} ${next}`.trim();
          return current.trim();

        case 'undefined':
        case 'boolean':
          return current;
      }
    }, '');

  return classNamesRec(values) || void 0;
};
